import {
  useEffect, useState, useRef, useContext,
} from 'react';
import { useSearchParams } from 'react-router';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import LinearProgress from '@mui/material/LinearProgress';

import { useGetInventoryAnalyticsMutation } from '#services/inventory';
import { FeedbackUIContext } from '#context/feedback-ui-context';

import AggregateKPIs from './AggregateKPIs';
import UnifiedTable from './UnifiedTable';

const PAGE_SIZE = 30;

type AnalyticsTrigger = (arg: Record<string, unknown>) => Promise<{ data?: unknown; error?: unknown }>;

interface TileFilterEntry {
  field: string;
  value: string;
}

interface ClickedTile {
  id: string;
  filterField: string | null;
}

interface AnalyticsCollection {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface AnalyticsReport {
  collectionId: string;
  rows: Array<Record<string, unknown>>;
  total: number;
  [key: string]: unknown;
}

interface AnalyticsData {
  reports: AnalyticsReport[];
  aggregateKPIs: Record<string, unknown> | null;
  categories: { collections: AnalyticsCollection[] };
}

// Maps tile IDs to server-side filter field/value — used to restore tileFilter from URL
const TILE_FILTER_MAP: Record<string, TileFilterEntry> = {
  fastMovingCount: { field: 'movementCategory', value: 'fast' },
  normalMovingCount: { field: 'movementCategory', value: 'normal' },
  slowMovingCount: { field: 'movementCategory', value: 'slow' },
  reorderSoonCount: { field: 'stockStatus', value: 'reorderSoon' },
  overstockedCount: { field: 'stockStatus', value: 'overstocked' },
  onTrackCount: { field: 'stockStatus', value: 'onTrack' },
};

export default function InventoryAnalytics() {
  const [getAnalyticsRaw] = useGetInventoryAnalyticsMutation();
  const getAnalytics = getAnalyticsRaw as unknown as AnalyticsTrigger;
  const [searchParams, setSearchParams] = useSearchParams();
  const { simpleSnackbar, showBackdrop, hideBackdrop } = useContext(FeedbackUIContext)!;
  const overlayActiveRef = useRef(false);

  // Capture initial URL params once on mount — stable ref so they don't re-trigger effects
  const initRef = useRef({
    period: Number(searchParams.get('period')) || 60,
    collections: searchParams.get('collections') || null,
    tile: searchParams.get('tile') || null,
  });

  const [collections, setCollections] = useState<AnalyticsCollection[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<Record<string, boolean>>({});
  const [timePeriodDays, setTimePeriodDays] = useState(initRef.current.period);
  const [reports, setReports] = useState<Record<string, AnalyticsReport>>({});
  const [aggregateKPIs, setAggregateKPIs] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTile, setActiveTile] = useState<string | null>(initRef.current.tile);
  const [hasLoaded, setHasLoaded] = useState(false);

  // tileFilter is derived — not stored as state — so URL is the single source of truth
  const tileFilter = activeTile ? (TILE_FILTER_MAP[activeTile] || null) : null;

  const sortedCollections = [...collections].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));

  const selectedCollectionIds = sortedCollections
    .filter((c) => selectedCollections[c.id])
    .map((c) => c.id);

  // Sync state → URL after initial load so the URL always reflects the current view.
  // Uses replace so filter/period/collection changes don't pile up as browser history entries —
  // the back button goes to the page before analytics, not to a previous filter state.
  useEffect(() => {
    if (!hasLoaded) return;
    const params: Record<string, string> = {};
    if (timePeriodDays !== 60) params.period = String(timePeriodDays);
    if (activeTile) params.tile = activeTile;
    const allIds = sortedCollections.map((c) => c.id);
    const selectedIds = allIds.filter((id) => selectedCollections[id]);
    // Only write collections param when it is a strict subset of all available
    if (selectedIds.length > 0 && selectedIds.length < allIds.length) {
      params.collections = selectedIds.join(',');
    }
    setSearchParams(params, { replace: true });
  }, [timePeriodDays, activeTile, selectedCollections, hasLoaded]);

  useEffect(() => {
    const isInitialLoading = !hasLoaded && loading;
    if (isInitialLoading && !overlayActiveRef.current) {
      showBackdrop();
      overlayActiveRef.current = true;
    } else if (!isInitialLoading && overlayActiveRef.current) {
      hideBackdrop();
      overlayActiveRef.current = false;
    }

    return () => {
      if (overlayActiveRef.current) {
        hideBackdrop();
        overlayActiveRef.current = false;
      }
    };
  }, [hasLoaded, loading, showBackdrop, hideBackdrop]);

  function fetchAnalytics(overrides: Record<string, unknown> = {}) {
    setLoading(true);
    const params = {
      collections: selectedCollectionIds,
      timePeriodDays,
      page: 0,
      pageSize: PAGE_SIZE,
      ...overrides,
    };
    return getAnalytics(params).then((res) => {
      setLoading(false);
      const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
      if (hasError) return;
      const { reports: rpts, aggregateKPIs: kpis, categories } = res.data as AnalyticsData;
      setCollections(categories.collections);
      const reportsMap = rpts.reduce<Record<string, AnalyticsReport>>((acc, r) => {
        acc[r.collectionId] = r;
        return acc;
      }, {});
      setReports(reportsMap);
      setAggregateKPIs(kpis);
    });
  }

  // Initial load — restores period, collection selection, and tile filter from URL params
  useEffect(() => {
    const { period, collections: colParam } = initRef.current;
    const initCollectionIds = colParam
      ? colParam.split(',').filter(Boolean)
      : [];

    setLoading(true);
    void getAnalytics({
      timePeriodDays: period,
      page: 0,
      pageSize: PAGE_SIZE,
      ...(initCollectionIds.length > 0 ? { collections: initCollectionIds } : {}),
    }).then((res) => {
      setLoading(false);
      const hasError = simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
      setHasLoaded(true);
      if (hasError) return;
      const { reports: rpts, aggregateKPIs: kpis, categories } = res.data as AnalyticsData;
      const cols = categories.collections;
      setCollections(cols);

      if (colParam) {
        const ids = new Set(initCollectionIds);
        setSelectedCollections(cols.reduce<Record<string, boolean>>((acc, c) => {
          acc[c.id] = ids.has(c.id);
          return acc;
        }, {}));
      } else {
        setSelectedCollections(cols.reduce<Record<string, boolean>>((acc, c) => {
          acc[c.id] = true;
          return acc;
        }, {}));
      }

      const reportsMap = rpts.reduce<Record<string, AnalyticsReport>>((acc, r) => {
        acc[r.collectionId] = r;
        return acc;
      }, {});
      setReports(reportsMap);
      setAggregateKPIs(kpis);
    });
  }, []);

  function handleTimePeriodChange(_: unknown, value: number | null) {
    if (!value) return;
    setTimePeriodDays(value);
    void fetchAnalytics({ timePeriodDays: value, collections: selectedCollectionIds });
  }

  function handleCollectionToggle(collectionId: string, checked: boolean) {
    const next = { ...selectedCollections, [collectionId]: checked };
    const ids = collections.filter((c) => next[c.id]).map((c) => c.id);
    if (ids.length === 0) return;
    setSelectedCollections(next);
    void fetchAnalytics({ collections: ids });
  }

  function handleExport() {
    const params = new URLSearchParams({
      timePeriodDays: String(timePeriodDays),
      collections: selectedCollectionIds.join(','),
    });
    window.open(`/api/export/inventoryAnalytics?${params.toString()}`, '_blank');
  }

  function handleTileClick(tile: ClickedTile) {
    if (!tile.filterField) return;
    setActiveTile((prev) => (prev === tile.id ? null : tile.id));
  }

  if (!hasLoaded) return null;

  return (
    <>
      <Box sx={{ typography: 'h7' }}>Inventory Analytics</Box>

      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 1,
      }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">Time period:</Typography>
          <ToggleButtonGroup
            value={timePeriodDays}
            exclusive
            onChange={handleTimePeriodChange}
            size="small"
          >
            <ToggleButton value={60}>60d</ToggleButton>
            <ToggleButton value={90}>90d</ToggleButton>
            <ToggleButton value={120}>120d</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <ButtonGroup variant="outlined" size="small">
          <Button onClick={handleExport} disabled={loading}>Export CSV</Button>
        </ButtonGroup>
      </Box>

      <Box sx={{ mb: 1 }}>
        {sortedCollections.map((c) => (
          <FormControlLabel
            key={c.id}
            control={(
              <Checkbox
                checked={!!selectedCollections[c.id]}
                onChange={(e, checked) => handleCollectionToggle(c.id, checked)}
              />
            )}
            label={c.name}
          />
        ))}
      </Box>

      {hasLoaded && loading && <LinearProgress sx={{ mb: 1 }} />}

      <AggregateKPIs
        kpis={aggregateKPIs}
        activeTile={activeTile}
        onTileClick={handleTileClick}
      />

      {sortedCollections.map((c) => {
        if (!selectedCollections[c.id]) return null;
        const report = reports[c.id];
        if (!report) return null;
        return (
          <UnifiedTable
            key={c.id}
            collection={c}
            report={report as unknown as Parameters<typeof UnifiedTable>[0]['report']}
            getAnalytics={getAnalytics}
            queryOptions={{ timePeriodDays, pageSize: PAGE_SIZE }}
            tileFilter={tileFilter}
          />
        );
      })}
    </>
  );
}
